import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BadgeComponent } from 'shared/components/BadgeComponent';
import { getBadgeProperties } from 'shared/components/getBadgeProperties';
import { FeedComponent } from 'features/feed/FeedComponent';
import { JourneyComponent } from 'shared/components/JourneyComponent';
import { ProfileIdentitySearch } from 'features/profiles/ProfileIdentitySearch';
import { GalleryGroupRarityCounts } from 'shared/components/RarityCountsDisplay';
import { useTalosData } from 'shared/context/TalosDataContext';
import { Badge } from 'features/catalog/useBadges';
import { getProfileFeedUpdates } from 'features/feed/useFeedUpdates';
import { Profile } from 'features/profiles/useProfiles';
import { LeaderboardExpandState } from 'shared/layout/Sidebar';
import { BadgeModes } from 'types/badge-modes';
import {
  getBadgeCollectionGroup,
  setBadgeCollectionGroup,
  subscribeBadgeCollectionGroup,
} from 'features/catalog/badge-collection-group';
import { getContributorIds } from 'features/catalog/badge-contributors';
import {
  BADGE_GROUPS,
  groupBackgroundGradient,
  slugifyGroupName,
  sortBadgeGroupsByGlobalClaimRate,
} from 'features/catalog/badge-groups';
import { findUniqueHighlightBadges, HIGHLIGHTS_SIZE } from 'features/catalog/badge-hash';
import { gradientFromHighlightBadges } from 'features/catalog/badge-hash-gradient';
import { isJourneyCompleted, JOURNEYS } from 'features/catalog/badge-journeys';
import { isBadgeReleased } from 'features/catalog/badge-release';
import { getInitialBadgeEnabledState } from 'features/catalog/badge-state';
import { formatLastUpdated, getRankDisplay, talosBadgesLogoURL } from 'config/constants';
import { getFirstPartOfEmail } from 'shared/lib/email';
import { contributionsPath, parseContributionsTab } from 'shared/lib/nav-paths';
import {
  compareBadgesByScoreAsc,
  compareBadgesByScoreDesc,
  compareForceSortLast,
  getJourneysSortedForProfile,
} from 'features/catalog/rarity';
import '../gallery/BadgeGalleryView.css';
import '../gallery/JourneysGalleryView.css';
import './ProfileCollectedView.css';

interface ProfileCollectedViewProps {
  profile: Profile;
  currentUserEmail: string | null;
  currentProfile: Profile | null;
  mode: 'collected' | 'submitted' | 'journeys' | 'profile_feed';
  setMode: (mode: BadgeModes) => void;
  setSelectedProfileID: (id: string | null) => void;
  onSelectBadge: (badge: Badge | null) => void;
  onClaimNewBadges: () => void;
  onSubmitBadgeIdea: () => void;
  onSelectProfile: (profileId: string) => void;
  onClearProfile?: () => void;
  navigate?: (path: string) => void;
  onBackdropHighlight?: (badgeIds: string[] | null) => void;
}

export const ProfileCollectedView: React.FC<ProfileCollectedViewProps> = ({
  profile,
  currentUserEmail,
  currentProfile,
  mode,
  setMode,
  setSelectedProfileID,
  onSelectBadge,
  onClaimNewBadges,
  onSubmitBadgeIdea,
  onSelectProfile,
  onClearProfile,
  navigate,
  onBackdropHighlight,
}) => {
  const { profilesMap, rankedProfiles, badgesMap, feedUpdates, contributorIndex } = useTalosData();
  const location = useLocation();
  const profilePathId = getFirstPartOfEmail(profile.email);
  const [submittedGroup, setSubmittedGroup] = useState(() => parseContributionsTab(location.hash));
  const [collectionGroup, setCollectionGroupState] = useState(() => getBadgeCollectionGroup());
  const isSubmitted = mode === 'submitted';
  const isJourneys = mode === 'journeys';
  const isProfileFeed = mode === 'profile_feed';
  const activeGroup = isSubmitted ? submittedGroup : collectionGroup;

  useEffect(() => subscribeBadgeCollectionGroup(() => setCollectionGroupState(getBadgeCollectionGroup())), []);

  const setActiveGroup = useCallback(
    (id: string) => {
      if (isSubmitted) {
        const tab = parseContributionsTab(id);
        setSubmittedGroup(tab);
        if (navigate) navigate(contributionsPath(profilePathId, tab));
        return;
      }
      setBadgeCollectionGroup(id);
    },
    [isSubmitted, navigate, profilePathId]
  );

  useEffect(() => {
    if (!isSubmitted) return;
    const tab = parseContributionsTab(location.hash);
    setSubmittedGroup((prev) => (prev === tab ? prev : tab));
  }, [isSubmitted, location.hash, location.pathname]);

  /** Same inclusion + same-day grant merge as the profile Feed tab body. */
  const profileFeedUpdates = useMemo(
    () => getProfileFeedUpdates(feedUpdates, profile, badgesMap),
    [feedUpdates, profile, badgesMap]
  );
  const profileFeedCount = profileFeedUpdates.length;

  const journeysCompleted = useMemo(
    () => JOURNEYS.filter((j) => isJourneyCompleted(j, profile.badgesIDs)).length,
    [profile.badgesIDs]
  );

  const sortedJourneys = useMemo(
    () => getJourneysSortedForProfile(profile.badgesIDs, profilesMap.values()),
    [profile.badgesIDs, profilesMap]
  );

  const enabledBadgesMap = useMemo(
    () => getInitialBadgeEnabledState(profile.badgesIDs, badgesMap),
    [profile.badgesIDs, badgesMap]
  );

  const { creditIds, designIds } = useMemo(
    () => getContributorIds(contributorIndex, profile.email),
    [contributorIndex, profile.email]
  );

  const submittedIdSet = useMemo(() => new Set([...creditIds, ...designIds]), [creditIds, designIds]);

  const coolestBadge = useMemo(
    () =>
      profile.badgesIDs
        .map((id) => badgesMap.get(id))
        .filter((b): b is Badge => !!b)
        .sort(compareBadgesByScoreAsc)[0] ?? null,
    [profile.badgesIDs, badgesMap]
  );

  const rank = useMemo(() => {
    const index = rankedProfiles.findIndex((p) => p.email === profile.email);
    return index >= 0 ? index + 1 : null;
  }, [rankedProfiles, profile.email]);

  const { groups, freeform, claimedCount, totalCount } = useMemo(() => {
    const groupedIds = new Set<string>();
    const resolved = sortBadgeGroupsByGlobalClaimRate(BADGE_GROUPS, badgesMap)
      .map((group) => {
        const badges = group.badgesIDs
          .map((id) => {
            groupedIds.add(id);
            return badgesMap.get(id);
          })
          .filter((b): b is Badge => !!b)
          .filter((b) => (isSubmitted ? submittedIdSet.has(b.id) : true))
          .sort((a, b) => compareForceSortLast(a, b) || compareBadgesByScoreDesc(a, b));
        const claimedBadges = badges.filter((b) =>
          isSubmitted ? submittedIdSet.has(b.id) : enabledBadgesMap.get(b.id)
        );
        const claimed = claimedBadges.length;
        const percentage = badges.length === 0 ? 0 : Math.round((claimed / badges.length) * 100);
        return {
          ...group,
          badges,
          claimed,
          claimedIds: claimedBadges.map((badge) => badge.id),
          percentage,
          slug: slugifyGroupName(group.name),
        };
      })
      .filter((group) => group.badges.length > 0);

    const freeformBadges = Array.from(badgesMap.values())
      .filter((badge) => !groupedIds.has(badge.id))
      .filter((badge) => (isSubmitted ? submittedIdSet.has(badge.id) : true))
      .sort(compareBadgesByScoreDesc);

    return {
      groups: resolved,
      freeform: freeformBadges,
      claimedCount: profile.badgesIDs.length,
      totalCount: badgesMap.size,
    };
  }, [badgesMap, enabledBadgesMap, profile.badgesIDs.length, isSubmitted, submittedIdSet]);

  const highlightBadges = useMemo(() => {
    if (isSubmitted) return [];
    const claimed = profile.badgesIDs.map((id) => badgesMap.get(id)).filter((b): b is Badge => !!b);
    if (claimed.length < HIGHLIGHTS_SIZE) return [];

    const ownId = getFirstPartOfEmail(profile.email);
    return findUniqueHighlightBadges(claimed, ownId) ?? [];
  }, [isSubmitted, profile.badgesIDs, profile.email, badgesMap]);

  const showHighlights = highlightBadges.length === HIGHLIGHTS_SIZE;
  const highlightsOwnershipPercentage = useMemo(() => {
    if (!showHighlights || profilesMap.size === 0) return 0;
    // Unique to this profile → ownership share rounds to 0 or <1 of total profiles.
    return Math.round((1 / profilesMap.size) * 100);
  }, [showHighlights, profilesMap.size]);
  const highlightsGradient = useMemo(
    () => (showHighlights ? gradientFromHighlightBadges(highlightBadges) : null),
    [highlightBadges, showHighlights]
  );

  const freeformClaimedBadges = useMemo(
    () => freeform.filter((b) => (isSubmitted ? submittedIdSet.has(b.id) : enabledBadgesMap.get(b.id))),
    [freeform, enabledBadgesMap, isSubmitted, submittedIdSet]
  );
  const freeformClaimed = freeformClaimedBadges.length;

  const ideaBadges = useMemo(
    () =>
      creditIds
        .map((id) => badgesMap.get(id))
        .filter((b): b is Badge => !!b)
        .sort(compareBadgesByScoreAsc),
    [creditIds, badgesMap]
  );

  const designBadges = useMemo(
    () =>
      designIds
        .map((id) => badgesMap.get(id))
        .filter((b): b is Badge => !!b)
        .sort(compareBadgesByScoreAsc),
    [designIds, badgesMap]
  );

  const allSubmittedBadges = useMemo(() => {
    const byId = new Map<string, Badge>();
    for (const badge of [...ideaBadges, ...designBadges]) byId.set(badge.id, badge);
    return [...byId.values()].sort(compareBadgesByScoreAsc);
  }, [ideaBadges, designBadges]);

  const navItems = useMemo(() => {
    if (isSubmitted) {
      return [
        { id: 'all', label: 'All' },
        { id: 'idea', label: 'Idea' },
        { id: 'design', label: 'Design' },
      ];
    }

    const items: {
      id: string;
      label: string;
      gradient?: string;
      percentage?: number;
    }[] = [
      { id: 'all', label: 'All' },
      {
        id: 'highlights',
        label: 'Badge Hash',
        gradient: highlightsGradient ?? undefined,
        percentage: showHighlights ? highlightsOwnershipPercentage : 0,
      },
    ];
    items.push(
      ...groups.map((g) => ({
        id: g.slug,
        label: g.name,
        gradient: groupBackgroundGradient(g),
        percentage: g.percentage,
      }))
    );
    if (freeform.length > 0) {
      const pct = freeform.length === 0 ? 0 : Math.round((freeformClaimed / freeform.length) * 100);
      items.push({ id: 'freeform', label: 'More Badges', percentage: pct });
    }
    return items;
  }, [
    isSubmitted,
    groups,
    freeform.length,
    freeformClaimed,
    showHighlights,
    highlightsGradient,
    highlightsOwnershipPercentage,
  ]);

  useEffect(() => {
    // Keep Badge Hash selected across profiles even when the new profile has no hash yet.
    if (activeGroup === 'highlights') return;
    if (activeGroup !== 'all' && !navItems.some((item) => item.id === activeGroup)) {
      setActiveGroup('all');
    }
  }, [activeGroup, navItems, setActiveGroup]);

  const isHighlights = activeGroup === 'highlights';
  const showHighlightsSection = !isSubmitted && showHighlights && (isHighlights || activeGroup === 'all');
  const showLockedHighlights =
    !isSubmitted && !isJourneys && !showHighlights && (isHighlights || activeGroup === 'all');
  const visibleGroups =
    isSubmitted || isHighlights || activeGroup === 'freeform'
      ? []
      : activeGroup === 'all'
        ? groups
        : groups.filter((g) => g.slug === activeGroup);
  const showFreeform =
    !isSubmitted && !isHighlights && (activeGroup === 'all' || activeGroup === 'freeform') && freeform.length > 0;

  const submittedFilterBadges =
    activeGroup === 'idea' ? ideaBadges : activeGroup === 'design' ? designBadges : allSubmittedBadges;
  const submittedFilterLabel =
    activeGroup === 'idea' ? 'Ideas' : activeGroup === 'design' ? 'Designs' : 'Contributions';
  const submittedFilterMeta =
    activeGroup === 'idea'
      ? `${ideaBadges.length} idea${ideaBadges.length === 1 ? '' : 's'}`
      : activeGroup === 'design'
        ? `${designBadges.length} design${designBadges.length === 1 ? '' : 's'}`
        : `${allSubmittedBadges.length} contribution${allSubmittedBadges.length === 1 ? '' : 's'}`;
  const isOwnProfile =
    currentUserEmail != null && getFirstPartOfEmail(currentUserEmail) === getFirstPartOfEmail(profile.email);
  const submittedCount = submittedIdSet.size;

  const setProfileMode = (next: 'collected' | 'submitted' | 'journeys' | 'profile_feed') => {
    if (navigate) {
      if (next === 'collected') navigate(`/${profilePathId}`);
      else if (next === 'profile_feed') navigate(`/${profilePathId}/feed`);
      else if (next === 'submitted') navigate(contributionsPath(profilePathId));
      else navigate(`/${profilePathId}/${next}`);
    }
    setMode(next);
  };

  // Reset scroll when switching profile tabs (Collected ↔ Journeys ↔ …) or profiles.
  useEffect(() => {
    const scroller =
      document.querySelector('.profile-collected-scroll') ??
      document.querySelector('.profile-collected-body .feed-page') ??
      document.querySelector('.profile-collected-body');
    if (scroller instanceof HTMLElement) {
      scroller.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [mode, profilePathId]);

  // Deep-link: /{id}#{group-slug} → group section, or /{id}#badge-{id} → that badge (More Badges).
  useEffect(() => {
    if (mode !== 'collected') return;
    const hash = location.hash.replace(/^#/, '');
    if (!hash) return;

    const badgeId = hash.startsWith('badge-') ? hash.slice('badge-'.length) : null;
    setActiveGroup('all');
    const timeout = window.setTimeout(
      () => {
        const el = document.getElementById(badgeId ? `collected-badge-${badgeId}` : `collected-${hash}`);
        const scroller =
          document.querySelector('.profile-collected-scroll') ?? document.querySelector('.profile-collected-body');
        if (!el) return;
        if (scroller instanceof HTMLElement) {
          const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 24;
          scroller.scrollTo({ top, behavior: 'smooth' });
        } else {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
      badgeId ? 120 : 80
    );

    return () => window.clearTimeout(timeout);
  }, [location.hash, location.pathname, mode, profile.email, setActiveGroup]);

  // Deep-link from feed: /{id}/journeys#{journeyId} → scroll to that finished journey.
  useEffect(() => {
    if (mode !== 'journeys') return;
    const hash = location.hash.replace(/^#/, '');
    if (!hash) return;
    if (!JOURNEYS.some((j) => j.id === hash)) return;

    const timeout = window.setTimeout(() => {
      const el = document.getElementById(hash);
      const scroller =
        document.querySelector('.profile-collected-scroll') ?? document.querySelector('.profile-collected-body');
      if (!el) return;
      if (scroller instanceof HTMLElement) {
        const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 24;
        scroller.scrollTo({ top, behavior: 'smooth' });
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 80);

    return () => window.clearTimeout(timeout);
  }, [location.hash, location.pathname, mode, profile.email]);

  const renderBadge = (badge: Badge) => {
    const props = getBadgeProperties({
      badge,
      mode,
      enabledBadgesMap,
      profileBadgeIDs: profile.badgesIDs,
      badgeCreditsSubmissions: creditIds,
      badgeDesignSubmissions: designIds,
    });
    const unreleased = !isBadgeReleased(badge.id);
    const owned = !!enabledBadgesMap.get(badge.id);
    const lockedUntilRelease = unreleased && !owned;

    return (
      <div key={badge.id} id={`collected-badge-${badge.id}`} className={`gallery-badge rarity-${badge.rarityName}`}>
        <BadgeComponent
          badge={badge}
          onClick={onSelectBadge}
          blackAndWhite={lockedUntilRelease || props.blackAndWhite}
          lowOpacity={props.lowOpacity}
          display={props.display}
          showName={!lockedUntilRelease && props.showName}
          hiddenName={lockedUntilRelease}
          cursorNotAllowed={lockedUntilRelease}
          showNameHeight
          smaller
          showFloatingNameShadow={false}
          ideaIncluded={props.ideaIncluded}
          designIncluded={props.designIncluded}
        />
      </div>
    );
  };

  return (
    <div className="gallery-page profile-collected-page">
      <div className="profile-collected-top">
        <ProfileIdentitySearch
          selectedProfileId={getFirstPartOfEmail(profile.email)}
          onSelectProfile={onSelectProfile}
          onClear={onClearProfile}
          avatarSrc={coolestBadge?.smallImageUrl ?? talosBadgesLogoURL}
          avatarBadge={coolestBadge}
          avatarAlt={coolestBadge ? coolestBadge.name : undefined}
          onAvatarClick={coolestBadge ? () => onSelectBadge(coolestBadge) : undefined}
        >
          <h1 className="visually-hidden">
            {rank != null ? `${getRankDisplay(rank)} ` : ''}
            {profile.name}
          </h1>
          <p className="gallery-subtitle">
            {profile.email}
            {' · '}
            <button
              type="button"
              className="profile-collected-updated-link"
              onClick={() => setProfileMode('profile_feed')}
              disabled={profileFeedCount === 0 && !isOwnProfile}
              aria-label={`View feed (updated ${formatLastUpdated(profile.lastActivityAt)})`}
            >
              {`updated ${formatLastUpdated(profile.lastActivityAt)}`}
            </button>
          </p>
          <div className="profile-collected-stats">
            <div className="profile-collected-stats-meta">
              <button
                type="button"
                className="profile-collected-badges-link"
                onClick={() => setProfileMode('collected')}
                aria-label={`View badges (${claimedCount} of ${totalCount})`}
              >
                <strong>{claimedCount}</strong> badges
              </button>
              {' · '}
              <Link
                to="/leaderboard"
                state={
                  {
                    expandProfileId: profilePathId,
                  } satisfies LeaderboardExpandState
                }
                className="profile-collected-pts"
                aria-label={`Open leaderboard for ${profile.name} (${profile.sortScore} pts)`}
              >
                <strong>{profile.sortScore}</strong> pts
              </Link>
              {' · '}
              <button
                type="button"
                className="profile-collected-journeys-link"
                onClick={() => setProfileMode('journeys')}
                aria-label={`View journeys (${journeysCompleted} completed)`}
              >
                <strong>{journeysCompleted}</strong> Journey{journeysCompleted === 1 ? '' : 's'}
              </button>
            </div>
            {profile.journeyEmojis.length > 0 ? (
              <button
                type="button"
                className="profile-collected-journeys"
                onClick={() => setProfileMode('journeys')}
                aria-label={`View journeys (${profile.journeyEmojis.length} completed)`}
              >
                {profile.journeyEmojis.map((emoji) => (
                  <span key={emoji}>{emoji}</span>
                ))}
              </button>
            ) : null}
          </div>
        </ProfileIdentitySearch>
      </div>

      <div className="profile-collected-body">
        <header className="profile-collected-header">
          <div className="gallery-nav profile-collected-modes" role="tablist" aria-label="Profile sections">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'collected'}
              className={`gallery-nav-chip ${mode === 'collected' ? 'active' : ''}`}
              onClick={() => setProfileMode('collected')}
            >
              Badges ({claimedCount}/{totalCount})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'journeys'}
              className={`gallery-nav-chip ${mode === 'journeys' ? 'active' : ''}`}
              onClick={() => setProfileMode('journeys')}
            >
              Journeys ({journeysCompleted}/{JOURNEYS.length})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'profile_feed'}
              className={`gallery-nav-chip ${mode === 'profile_feed' ? 'active' : ''}${
                profileFeedCount === 0 && !isOwnProfile ? ' disabled' : ''
              }`}
              onClick={() => setProfileMode('profile_feed')}
              disabled={profileFeedCount === 0 && !isOwnProfile}
            >
              Feed ({profileFeedCount})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'submitted'}
              className={`gallery-nav-chip ${mode === 'submitted' ? 'active' : ''}${
                submittedCount === 0 && !isOwnProfile ? ' disabled' : ''
              }`}
              onClick={() => setProfileMode('submitted')}
              disabled={submittedCount === 0 && !isOwnProfile}
            >
              Contributions ({submittedCount})
            </button>
            {isOwnProfile && !isSubmitted ? (
              <button type="button" className="gallery-nav-chip profile-collected-claim" onClick={onClaimNewBadges}>
                + Claim New Badges
              </button>
            ) : null}
            {isOwnProfile && isSubmitted ? (
              <button type="button" className="gallery-nav-chip profile-collected-claim" onClick={onSubmitBadgeIdea}>
                + Submit a Badge Idea
              </button>
            ) : null}
          </div>
        </header>

        {isProfileFeed ? (
          <FeedComponent
            currentProfile={currentProfile}
            lockedProfileId={profilePathId}
            setSelectedProfileID={setSelectedProfileID}
            setSelectedBadge={onSelectBadge}
            onBackdropHighlight={onBackdropHighlight}
          />
        ) : (
          <div className="profile-collected-scroll">
            {isJourneys ? (
              <div className="journeys-light-grid profile-journeys-grid">
                {sortedJourneys.map((journey) => (
                  <JourneyComponent
                    key={journey.id}
                    journey={journey}
                    badgesMap={badgesMap}
                    badgeIDs={profile.badgesIDs}
                    onClickBadge={onSelectBadge}
                  />
                ))}
              </div>
            ) : isSubmitted && submittedCount === 0 ? (
              <p className="profile-collected-empty">No badge ideas or designs contributed yet.</p>
            ) : (
              <>
                <div
                  className="gallery-nav"
                  role="tablist"
                  aria-label={isSubmitted ? 'Contribution filters' : 'Badge collections'}
                >
                  {navItems.map((item) => {
                    const pct = item.percentage ?? 0;
                    const hasFill = !isSubmitted && Boolean(item.gradient) && pct > 0;
                    const isHighlightsChip = item.id === 'highlights';
                    // Highlights: always full fill. Categories: completion slant (wider at bottom).
                    const clipPath = isHighlightsChip
                      ? undefined
                      : `polygon(0 0, ${pct}% 0, ${pct + 20}% 100%, 0 100%)`;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="tab"
                        aria-selected={activeGroup === item.id}
                        className={`gallery-nav-chip ${activeGroup === item.id ? 'active' : ''}${
                          !isSubmitted && item.gradient != null ? ' gallery-nav-chip--progress' : ''
                        }`}
                        onClick={() => setActiveGroup(item.id)}
                      >
                        {hasFill ? (
                          <span
                            className="gallery-nav-chip-fill"
                            aria-hidden
                            style={{
                              background: item.gradient,
                              opacity: 0.22,
                              ...(clipPath ? { clipPath } : null),
                            }}
                          />
                        ) : null}
                        <span className="gallery-nav-chip-label">{item.label}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="gallery-sections">
                  {isSubmitted ? (
                    <section className="gallery-group gallery-group--freeform">
                      <header className="gallery-group-head">
                        <span className="gallery-group-swatch gallery-group-swatch--muted" aria-hidden />
                        <div className="gallery-group-titles">
                          <h2 className="gallery-group-name">{submittedFilterLabel}</h2>
                          <p className="gallery-group-meta">{submittedFilterMeta}</p>
                        </div>
                        <GalleryGroupRarityCounts
                          badgeIds={submittedFilterBadges.map((badge) => badge.id)}
                          badgesMap={badgesMap}
                        />
                      </header>
                      {submittedFilterBadges.length > 0 ? (
                        <div className="gallery-badge-grid">{submittedFilterBadges.map(renderBadge)}</div>
                      ) : (
                        <p className="profile-collected-empty">No {submittedFilterLabel.toLowerCase()} yet.</p>
                      )}
                    </section>
                  ) : (
                    <>
                      {showHighlightsSection && highlightsGradient ? (
                        <section className="gallery-group gallery-group--highlights">
                          <div className="gallery-group-fill" aria-hidden>
                            <div
                              className="gallery-group-fill-inner gallery-group-fill-inner--highlights"
                              style={{
                                background: highlightsGradient,
                              }}
                            />
                          </div>
                          <div className="gallery-group-content">
                            <header className="gallery-group-head">
                              <span
                                className="gallery-group-swatch"
                                style={{ background: highlightsGradient }}
                                aria-hidden
                              />
                              <div className="gallery-group-titles">
                                <h2 className="gallery-group-name">Badge Hash</h2>
                                <p className="gallery-group-meta">Nobody else has this combination of badges</p>
                              </div>
                              <GalleryGroupRarityCounts
                                badgeIds={highlightBadges.map((badge) => badge.id)}
                                badgesMap={badgesMap}
                              />
                            </header>
                            <div className="gallery-badge-grid">{highlightBadges.map(renderBadge)}</div>
                          </div>
                        </section>
                      ) : showLockedHighlights ? (
                        <section className="gallery-group gallery-group--badge-hash-locked">
                          <header className="gallery-group-head">
                            <span className="gallery-group-swatch gallery-group-swatch--muted" aria-hidden />
                            <div className="gallery-group-titles">
                              <h2 className="gallery-group-name">Badge Hash</h2>
                              <p className="gallery-group-meta">
                                No Badge Hash yet — claim more unique badges to own a hash.
                              </p>
                            </div>
                          </header>
                        </section>
                      ) : null}

                      {visibleGroups.map((group) => (
                        <section key={group.slug} id={`collected-${group.slug}`} className="gallery-group">
                          <div className="gallery-group-fill" aria-hidden>
                            <div
                              className="gallery-group-fill-inner"
                              style={{
                                background: groupBackgroundGradient(group),
                                opacity: group.percentage === 0 ? 0 : 0.28 + (group.percentage / 100) * 0.28,
                                clipPath: `polygon(0 0, ${group.percentage}% 0, ${
                                  group.percentage === 0 ? 0 : group.percentage + 20
                                }% 100%, 0 100%)`,
                              }}
                            />
                          </div>
                          <div className="gallery-group-content">
                            <header className="gallery-group-head">
                              <span
                                className="gallery-group-swatch"
                                style={{
                                  background: groupBackgroundGradient(group, '160deg'),
                                }}
                                aria-hidden
                              />
                              <div className="gallery-group-titles">
                                <h2 className="gallery-group-name">{group.name}</h2>
                                <p className="gallery-group-meta">
                                  {`${group.claimed}/${group.badges.length} claimed · ${group.percentage}%`}
                                </p>
                              </div>
                              <GalleryGroupRarityCounts badgeIds={group.claimedIds} badgesMap={badgesMap} />
                            </header>
                            <div className="gallery-badge-grid">{group.badges.map(renderBadge)}</div>
                          </div>
                        </section>
                      ))}

                      {showFreeform ? (
                        <section id="collected-freeform" className="gallery-group gallery-group--freeform">
                          <header className="gallery-group-head">
                            <span className="gallery-group-swatch gallery-group-swatch--muted" aria-hidden />
                            <div className="gallery-group-titles">
                              <h2 className="gallery-group-name">More Badges</h2>
                              <p className="gallery-group-meta">{`${freeformClaimed}/${freeform.length} claimed`}</p>
                            </div>
                            <GalleryGroupRarityCounts
                              badgeIds={freeformClaimedBadges.map((badge) => badge.id)}
                              badgesMap={badgesMap}
                            />
                          </header>
                          <div className="gallery-badge-grid">{freeform.map(renderBadge)}</div>
                        </section>
                      ) : null}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
